import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TenantConfigContext = createContext(null);
export const useTenantConfig = () => useContext(TenantConfigContext);

// NBFC defaults => existing tenants (e.g. TinyLoan) are completely unchanged.
const DEFAULT_CONFIG = {
  industry: "nbfc",
  name: "NBFC / Lender",
  terms: {
    entity: "Borrower",
    entity_plural: "Borrowers",
    identifier: "Loan ID",
    amount: "Outstanding",
    nav_accounts: "Accounts",
    account_noun: "Loan Account",
  },
  modules: ["work_queue", "leaderboard", "settlements", "legal", "communications", "providers", "workflows", "payments", "reports", "ai_scoring"],
  custom_fields: [],
  branding: { logo: "", product_name: "", accent_color: "", company_name: "" },
};

const DEFAULT_PRIMARY = "223 100% 33%";

function hexToHslChannels(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyAccent(hex) {
  const channels = hexToHslChannels(hex);
  const root = document.documentElement;
  root.style.setProperty("--primary", channels || DEFAULT_PRIMARY);
  root.style.setProperty("--ring", channels || DEFAULT_PRIMARY);
}

export function TenantConfigProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    if (!user) { applyAccent(null); return; }
    const load = () => api.get("/tenant-config")
      .then((r) => {
        const cfg = {
          ...DEFAULT_CONFIG,
          ...r.data,
          terms: { ...DEFAULT_CONFIG.terms, ...(r.data.terms || {}) },
          branding: { ...DEFAULT_CONFIG.branding, ...(r.data.branding || {}) },
        };
        setConfig(cfg);
        applyAccent(cfg.branding.accent_color);
      })
      .catch(() => { setConfig(DEFAULT_CONFIG); applyAccent(null); });
    load();
    const handler = () => load();
    window.addEventListener("recoveros:config-changed", handler);
    return () => window.removeEventListener("recoveros:config-changed", handler);
  }, [user]);

  const hasModule = (m) => config.modules.includes(m);

  return (
    <TenantConfigContext.Provider value={{ config, terms: config.terms, branding: config.branding, hasModule }}>
      {children}
    </TenantConfigContext.Provider>
  );
}
